import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  IconButton,
  Fade,
  Skeleton,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import InboxIcon from "@mui/icons-material/Inbox";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { styled } from "@mui/material/styles";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import AddUser from "./AddUser";
import { EnquiryContext } from "../Contexts/EnquiryContext";
import { url } from "../Components/url";
import { fetchWithAuth } from "../Components/FetchWithAuth";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useListViewMode } from "../hooks/useListPreferences";
import { GlobalSearchBar } from "../Components/ERPList";
import styles from "./AllEnquiries.module.scss";

const LIST_ID = "users";

const PAGE_SIZE_OPTIONS = [10, 20, 25, 50, 100];

const theme = createTheme({
  palette: {
    primary: { main: "#5e72e4" },
    background: { default: "#f8f9fe" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
});

const StyledCard = styled(Card)(({ theme }) => ({
  cursor: "pointer",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[10],
  },
}));

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: "#ffffff",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

function RoleChips({ roleString }) {
  const parts = (roleString || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" component="span">
        —
      </Typography>
    );
  }
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ maxWidth: "100%", minWidth: 0 }}>
      {parts.map((r) => (
        <Chip key={r} label={r} size="small" variant="outlined" color="primary" />
      ))}
    </Stack>
  );
}

function Users() {
  const { setEnquiry } = useContext(EnquiryContext);
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [cardData, setCardData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchData, setSearchData] = useState({
    id: "",
    email: "",
    role: "",
    phone: "",
    status: "",
  });
  const debouncedSearchData = useDebouncedValue(searchData, 400);
  const [show, setShow] = useState(false);
  const [filterOpen, setFilterOpen] = useState(!isMobile);
  const [loading, setLoading] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");
  const debouncedGlobalSearch = useDebouncedValue(globalSearch, 400);
  const [viewMode, setViewMode] = useListViewMode(LIST_ID, "table");
  const toggleViewMode = () => setViewMode((prev) => (prev === "cards" ? "table" : "cards"));
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("time_created");
  const [refreshTick, setRefreshTick] = useState(0);

  const filterKey = useMemo(
    () =>
      JSON.stringify({
        g: debouncedGlobalSearch,
        ...debouncedSearchData,
      }),
    [debouncedGlobalSearch, debouncedSearchData]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filterKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErrorMessage("");
      try {
        const qs = new URLSearchParams({
          page: String(currentPage),
          page_size: String(pageSize),
          sort_by: orderBy,
          sort_dir: order,
        });
        const g = (debouncedGlobalSearch || "").trim();
        if (g) qs.set("search", g);
        if ((debouncedSearchData.id || "").trim()) qs.set("user_id", debouncedSearchData.id.trim());
        if ((debouncedSearchData.email || "").trim()) qs.set("email", debouncedSearchData.email.trim());
        if ((debouncedSearchData.role || "").trim()) qs.set("role", debouncedSearchData.role.trim());
        if ((debouncedSearchData.phone || "").trim()) qs.set("phone", debouncedSearchData.phone.trim());
        if ((debouncedSearchData.status || "").trim()) qs.set("status", debouncedSearchData.status.trim());

        const response = await fetchWithAuth(`${url}/get_users?${qs}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        if (cancelled) return;
        if (response.ok) {
          const data = await response.json();
          const list = Array.isArray(data) ? data : (data?.users ?? []);
          setCardData(list);
          setTotalCount(typeof data?.total === "number" ? data.total : list.length);
        } else {
          const errorData = await response.json().catch(() => ({}));
          setErrorMessage(errorData.error || "Failed to load users.");
          toast.error(errorData.error || "Failed to load users.");
          setCardData([]);
          setTotalCount(0);
        }
      } catch {
        if (!cancelled) {
          setErrorMessage("An error occurred. Please try again.");
          toast.error("An error occurred. Please try again.");
          setCardData([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    currentPage,
    pageSize,
    debouncedGlobalSearch,
    debouncedSearchData,
    orderBy,
    order,
    refreshTick,
  ]);

  const handleViewEnquiry = (enquiry) => {
    const dataDictionary = Array.isArray(enquiry)
      ? enquiry.reduce((acc, item, index) => {
          acc[index] = item;
          return acc;
        }, {})
      : enquiry;
    setEnquiry(dataDictionary);
    navigate("/userProfile", { state: { enquiry } });
  };

  const displayRows = cardData;
  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / pageSize) || 1);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const safePage = Math.min(currentPage, totalPages);
  const rangeStart = totalCount === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, totalCount);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleRefresh = () => setRefreshTick((t) => t + 1);

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root}>
        <Paper elevation={0} className={styles.contentPaper}>
          <Box sx={{ marginBottom: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box>
                <Typography variant="h4" className={styles.pageTitle}>
                  Users
                </Typography>
                <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                  All accounts in the system. Search, filter, sort, and paginate on the server.
                </Typography>
              </Box>
            </Stack>
          </Box>
          <Box className={styles.header}>
            <GlobalSearchBar
              value={globalSearch}
              onChange={setGlobalSearch}
              placeholder="Search name, username, email, phone, ID…"
            />
            <Box className={styles.headerActions}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setFilterOpen(!filterOpen)}
                sx={{ textTransform: "none" }}
              >
                {filterOpen ? "Hide Filters" : "Show Filters"}
              </Button>
              <Tooltip title={viewMode === "cards" ? "Table View" : "Card View"}>
                <IconButton onClick={toggleViewMode} color="primary">
                  {viewMode === "cards" ? <ViewListIcon /> : <ViewModuleIcon />}
                </IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setShow(true)}>
                Add User
              </Button>
            </Box>
          </Box>

          {filterOpen && (
            <Fade in={filterOpen}>
              <FilterPaper variant="outlined">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="ID"
                      value={searchData.id}
                      onChange={(e) => setSearchData((p) => ({ ...p, id: e.target.value }))}
                      inputProps={{ "aria-label": "Filter by user ID" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Email"
                      type="email"
                      value={searchData.email}
                      onChange={(e) => setSearchData((p) => ({ ...p, email: e.target.value }))}
                      inputProps={{ "aria-label": "Filter by email" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Role name contains"
                      value={searchData.role}
                      onChange={(e) => setSearchData((p) => ({ ...p, role: e.target.value }))}
                      placeholder="e.g. Visa, Counsellor"
                      inputProps={{ "aria-label": "Filter by role name" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Phone"
                      value={searchData.phone}
                      onChange={(e) => setSearchData((p) => ({ ...p, phone: e.target.value }))}
                      inputProps={{ "aria-label": "Filter by phone" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Status"
                      value={searchData.status}
                      onChange={(e) => setSearchData((p) => ({ ...p, status: e.target.value }))}
                      inputProps={{ "aria-label": "Filter by status" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} className={styles.filterActions}>
                    <Button variant="contained" onClick={handleRefresh}>
                      Refresh
                    </Button>
                  </Grid>
                </Grid>
              </FilterPaper>
            </Fade>
          )}

          {errorMessage && (
            <Typography color="error" align="center" sx={{ my: 2 }}>
              {errorMessage}
            </Typography>
          )}

          {loading && (
            <Box className={styles.loadingContainer}>
              {viewMode === "cards" ? (
                <Grid container spacing={2}>
                  {[...Array(6)].map((_, i) => (
                    <Grid item xs={12} sm={6} md={4} key={i}>
                      <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box>
                  <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
                  <Skeleton variant="rectangular" height={400} />
                </Box>
              )}
            </Box>
          )}

          {!loading && totalCount === 0 && (
            <Box className={styles.emptyState}>
              <InboxIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No users found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting filters or search.
              </Typography>
            </Box>
          )}

          {!loading && totalCount > 0 && (
            <>
              <Box className={styles.pagination}>
                <Typography variant="body2" color="text.secondary">
                  Showing {rangeStart}–{rangeEnd} of {totalCount} users
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="users-page-size-label">Per page</InputLabel>
                    <Select
                      labelId="users-page-size-label"
                      value={pageSize}
                      label="Per page"
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Pagination
                    count={totalPages}
                    page={safePage}
                    onChange={(_, p) => setCurrentPage(p)}
                    color="primary"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              </Box>

              {displayRows.length === 0 ? (
                <Box className={styles.emptyState}>
                  <InboxIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No rows on this page
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Go to the first page or change how many rows you show per page.
                  </Typography>
                </Box>
              ) : viewMode === "cards" ? (
                <Grid container spacing={2} className={styles.cardsGrid}>
                  {displayRows.map((card) => (
                    <Grid item xs={12} sm={6} md={4} key={card?.id ?? card?.username}>
                      <StyledCard onClick={() => handleViewEnquiry(card)}>
                        <CardHeader
                          avatar={
                            <Avatar sx={{ bgcolor: "primary.main" }}>
                              {card?.first_name?.charAt(0) || "U"}
                            </Avatar>
                          }
                          title={`${card?.first_name || "N/A"} ${card?.last_name || ""}`}
                          subheader={card?.email || "—"}
                          className={styles.cardHeader}
                        />
                        <CardContent className={styles.cardContent}>
                          <Typography variant="body2">
                            <strong>Email:</strong> {card?.email || "N/A"}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Phone:</strong> {card?.phone_number || "N/A"}
                          </Typography>
                          <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                            <strong>Roles:</strong> <RoleChips roleString={card?.role} />
                          </Typography>
                          <Typography variant="body2">
                            <strong>Status:</strong> {card?.current_status || "N/A"}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Time Created:</strong> {card?.time_created || "N/A"}
                          </Typography>
                        </CardContent>
                      </StyledCard>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Paper variant="outlined" className={styles.tableContainer}>
                  <Table stickyHeader size="small" className={styles.enquiriesTable}>
                    <TableHead>
                      <TableRow>
                        <TableCell className={styles.colUserId} sortDirection={orderBy === "id" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "id"}
                            direction={orderBy === "id" ? order : "asc"}
                            onClick={() => handleRequestSort("id")}
                          >
                            ID
                          </TableSortLabel>
                        </TableCell>
                        <TableCell className={styles.colUserName} sortDirection={orderBy === "name" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "name"}
                            direction={orderBy === "name" ? order : "asc"}
                            onClick={() => handleRequestSort("name")}
                          >
                            Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell className={styles.colUserEmail} sortDirection={orderBy === "email" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "email"}
                            direction={orderBy === "email" ? order : "asc"}
                            onClick={() => handleRequestSort("email")}
                          >
                            Email
                          </TableSortLabel>
                        </TableCell>
                        <TableCell className={styles.colUserRole} sortDirection={orderBy === "role" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "role"}
                            direction={orderBy === "role" ? order : "asc"}
                            onClick={() => handleRequestSort("role")}
                          >
                            Role
                          </TableSortLabel>
                        </TableCell>
                        <TableCell className={styles.colUserPhone} sortDirection={orderBy === "phone" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "phone"}
                            direction={orderBy === "phone" ? order : "asc"}
                            onClick={() => handleRequestSort("phone")}
                          >
                            Phone
                          </TableSortLabel>
                        </TableCell>
                        <TableCell className={styles.colUserStatus} sortDirection={orderBy === "status" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "status"}
                            direction={orderBy === "status" ? order : "asc"}
                            onClick={() => handleRequestSort("status")}
                          >
                            Status
                          </TableSortLabel>
                        </TableCell>
                        <TableCell className={styles.colUserTime} sortDirection={orderBy === "time_created" ? order : false}>
                          <TableSortLabel
                            active={orderBy === "time_created"}
                            direction={orderBy === "time_created" ? order : "asc"}
                            onClick={() => handleRequestSort("time_created")}
                          >
                            Time Created
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayRows.map((card) => (
                        <TableRow
                          key={card?.id ?? card?.username}
                          hover
                          onClick={() => handleViewEnquiry(card)}
                          className={styles.tableRow}
                        >
                          <TableCell className={styles.colUserId}>{card?.id}</TableCell>
                          <TableCell className={styles.colUserName}>
                            <Box className={styles.nameCell}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main", fontSize: 14 }}>
                                {card?.first_name?.charAt(0) || "U"}
                              </Avatar>
                              <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                                <Typography variant="body2" className={styles.primaryText} noWrap>
                                  {card?.first_name} {card?.last_name}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell className={`${styles.colUserEmail} ${styles.secondaryText}`}>
                            <Tooltip
                              title={card?.email ? String(card.email) : ""}
                              disableHoverListener={!card?.email}
                            >
                              <Box component="span" className={styles.colUserEmailInner}>
                                {card?.email || "—"}
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell className={styles.colUserRole} onClick={(e) => e.stopPropagation()}>
                            <RoleChips roleString={card?.role} />
                          </TableCell>
                          <TableCell className={styles.colUserPhone}>{card?.phone_number || "—"}</TableCell>
                          <TableCell className={styles.colUserStatus}>{card?.current_status || "—"}</TableCell>
                          <TableCell className={styles.colUserTime}>{card?.time_created || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </>
          )}
        </Paper>
      </Box>

      <Modal show={show} onHide={() => setShow(false)} fullscreen>
        <Modal.Header closeButton>
          <Modal.Title>Add User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AddUser />
        </Modal.Body>
      </Modal>
    </ThemeProvider>
  );
}

export default Users;
